import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import Modal from '../components/Modal';
import { 
  Users, 
  Plus, 
  Edit, 
  Trash2, 
  Phone,
  MessageCircle,
  Filter,
  Search,
  Star
} from 'lucide-react';

function Fornitori() {
  const { user } = useAuth();
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoria, setSelectedCategoria] = useState('');
  const [categorie, setCategorie] = useState([]); // Categorie da tutti i fornitori (per filtri)
  const [categorieDisponibili, setCategorieDisponibili] = useState([]); // Categorie dalla tabella (per checkbox)
  const [fornitoriDefault, setFornitoriDefault] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [editingFornitore, setEditingFornitore] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    telefono: '',
    categorie: [],
    whatsapp: '',
    intro_custom: '',
    outro_custom: ''
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fornitoreToDelete, setFornitoreToDelete] = useState(null);

  useEffect(() => {
    const initData = async () => {
      await loadFornitori();
      // Carica prima le categorie disponibili (che popola anche il filtro)
      await loadCategorieDisponibili();
      // Carica i fornitori default dopo che le categorie sono caricate
      // loadFornitoriDefault dipende da categorieDisponibili
    };
    initData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carica fornitori default quando categorieDisponibili cambia
  useEffect(() => {
    if (categorieDisponibili.length > 0) {
      loadFornitoriDefault();
    }
  }, [categorieDisponibili]);

  const loadFornitori = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/fornitori', {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setFornitori(response.data.data);
      }
    } catch (error) {
      console.error('Errore caricamento fornitori:', error);
      toast.error('Errore nel caricamento dei fornitori');
    } finally {
      setLoading(false);
    }
  };

  const loadCategorieDisponibili = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/categorie', {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('Risposta API categorie:', response.data);

      if (response.data.success && response.data.data) {
        // L'API già filtra per attivo = 1, quindi non serve filtrare di nuovo
        const categorieAttive = response.data.data.map(cat => cat.nome);
        console.log('Categorie attive caricate:', categorieAttive);
        setCategorieDisponibili(categorieAttive);
        // Usa le stesse categorie per il filtro
        setCategorie(categorieAttive);
      } else {
        console.warn('Nessuna categoria restituita dall\'API:', response.data);
        setCategorieDisponibili([]);
        setCategorie([]);
      }
    } catch (error) {
      console.error('Errore caricamento categorie disponibili:', error);
      console.error('Dettagli errore:', error.response?.data || error.message);
      // Fallback: prova a caricare dai fornitori se la route categorie non funziona
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/api/fornitori/categorie', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          const fallbackCats = Array.isArray(response.data.data) ? response.data.data : [];
          console.log('Categorie caricate da fallback (fornitori):', fallbackCats);
          setCategorieDisponibili(fallbackCats);
          setCategorie(fallbackCats);
        }
      } catch (fallbackError) {
        console.error('Errore caricamento categorie (fallback):', fallbackError);
        setCategorieDisponibili([]);
        setCategorie([]);
      }
    }
  };

  const loadCategorie = async () => {
    // Non serve più, ora è gestito da loadCategorieDisponibili
    // Manteniamo per compatibilità ma non fa nulla
  };

  const loadFornitoriDefault = async () => {
    if (categorieDisponibili.length === 0) {
      return; // Non fare nulla se non ci sono categorie
    }
    
    try {
      const token = localStorage.getItem('token');
      const defaults = {};
      
      // Carica fornitore default per ogni categoria
      for (const cat of categorieDisponibili) {
        try {
          const response = await axios.get(`/api/fornitori/default/${cat}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.success && response.data.data) {
            defaults[cat] = response.data.data.id;
          }
        } catch (err) {
          // Categoria senza default, ignora
        }
      }
      
      setFornitoriDefault(defaults);
    } catch (error) {
      console.error('Errore caricamento fornitori default:', error);
    }
  };

  const setFornitoreDefault = async (categoria, fornitoreId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post('/api/fornitori/default', {
        categoria,
        fornitore_id: fornitoreId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success(`Fornitore impostato come preferito per ${categoria.replace('_', ' ')}`);
        loadFornitoriDefault();
      }
    } catch (error) {
      console.error('Errore impostazione fornitore default:', error);
      toast.error('Errore nell\'impostazione del fornitore preferito');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('Form data al submit:', formData);
    
    // Validazione più dettagliata
    if (!formData.nome || formData.nome.trim() === '') {
      toast.error('Il nome è obbligatorio');
      return;
    }
    
    if (!formData.telefono || formData.telefono.trim() === '') {
      toast.error('Il telefono è obbligatorio');
      return;
    }
    
    if (!Array.isArray(formData.categorie) || formData.categorie.length === 0) {
      toast.error('Devi selezionare almeno una categoria');
      console.error('Categorie:', formData.categorie, 'Tipo:', typeof formData.categorie);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Assicurati che categorie sia un array valido e whatsapp usi telefono se vuoto
      const payload = {
        ...formData,
        categorie: Array.isArray(formData.categorie) ? formData.categorie : [],
        whatsapp: formData.whatsapp?.trim() || formData.telefono?.trim() || '',
        intro_custom: formData.intro_custom?.trim() || '',
        outro_custom: formData.outro_custom?.trim() || ''
      };
      
      console.log('Payload inviato:', payload);
      
      if (editingFornitore) {
        // Aggiorna fornitore esistente
        const response = await axios.put(`/api/fornitori/${editingFornitore.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Fornitore aggiornato con successo');
          loadFornitori();
        }
      } else {
        // Crea nuovo fornitore
        const response = await axios.post('/api/fornitori', payload, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.success) {
          toast.success('Fornitore creato con successo');
          loadFornitori();
        }
      }

      setShowForm(false);
      setEditingFornitore(null);
      setFormData({ nome: '', telefono: '', categorie: [], whatsapp: '', intro_custom: '', outro_custom: '' });
    } catch (error) {
      console.error('Errore salvataggio fornitore:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Errore nel salvataggio del fornitore');
      }
    }
  };

  const editFornitore = async (fornitore) => {
    // Se ci sono categorie nel fornitore che non sono disponibili (disattivate), aggiungiamole temporaneamente
    const categorieFornitore = Array.isArray(fornitore.categorie) ? fornitore.categorie : [];
    const categorieMancanti = categorieFornitore.filter(cat => !categorieDisponibili.includes(cat));
    
    if (categorieMancanti.length > 0) {
      // Carica tutte le categorie per trovare quelle mancanti
      try {
        const token = localStorage.getItem('token');
        const allCatsRes = await axios.get('/api/categorie/admin/list', {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (allCatsRes.data.success) {
          const catMancantiNomi = categorieMancanti.map(cat => {
            const found = allCatsRes.data.data.find(c => c.nome === cat);
            return found ? found.nome : cat;
          }).filter(Boolean);
          setCategorieDisponibili(prev => [...new Set([...prev, ...catMancantiNomi])]);
        }
      } catch (error) {
        console.error('Errore caricamento categorie:', error);
      }
    }
    
    setEditingFornitore(fornitore);
    setFormData({
      nome: fornitore.nome,
      telefono: fornitore.telefono,
      categorie: categorieFornitore,
      whatsapp: fornitore.whatsapp || '',
      intro_custom: fornitore.intro_custom || '',
      outro_custom: fornitore.outro_custom || ''
    });
    setShowForm(true);
  };
  
  const toggleCategoria = (categoria) => {
    setFormData(prev => {
      // Assicurati che categorie sia sempre un array
      const categorieAttuali = Array.isArray(prev.categorie) ? prev.categorie : [];
      const nuoveCategorie = categorieAttuali.includes(categoria)
        ? categorieAttuali.filter(c => c !== categoria)
        : [...categorieAttuali, categoria];
      
      console.log('Toggle categoria:', categoria, 'Nuove categorie:', nuoveCategorie);
      
      return { ...prev, categorie: nuoveCategorie };
    });
  };

  const deleteFornitore = (fornitoreId) => {
    const fornitore = fornitori.find(f => f.id === fornitoreId);
    setFornitoreToDelete(fornitore);
    setShowDeleteModal(true);
  };

  const executeDeleteFornitore = async () => {
    if (!fornitoreToDelete) return;

    setShowDeleteModal(false);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/api/fornitori/${fornitoreToDelete.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        toast.success('Fornitore eliminato con successo');
        loadFornitori();
      }
    } catch (error) {
      console.error('Errore eliminazione fornitore:', error);
      toast.error('Errore nell\'eliminazione del fornitore');
    } finally {
      setFornitoreToDelete(null);
    }
  };

  const callFornitore = (telefono) => {
    window.open(`tel:${telefono}`, '_self');
  };

  const messageFornitore = (whatsapp) => {
    const numero = whatsapp || '';
    window.open(`https://wa.me/${numero}`, '_blank');
  };

  const filteredFornitori = fornitori.filter(fornitore => {
    const matchesSearch = fornitore.nome.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategoria = !selectedCategoria || 
      (Array.isArray(fornitore.categorie) && fornitore.categorie.includes(selectedCategoria));
    return matchesSearch && matchesCategoria;
  });

  // Raggruppa fornitori per prima categoria (o mostra tutti se nessun filtro)
  const groupedFornitori = filteredFornitori.reduce((acc, fornitore) => {
    const categoriePrincipale = Array.isArray(fornitore.categorie) && fornitore.categorie.length > 0 
      ? fornitore.categorie[0] 
      : 'Senza categoria';
    
    if (!acc[categoriePrincipale]) {
      acc[categoriePrincipale] = [];
    }
    acc[categoriePrincipale].push(fornitore);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Fornitori</h1>
          <p className="mt-1 text-sm text-gray-500">
            {user?.ruolo === 'admin' 
              ? 'Gestisci i fornitori e le loro informazioni di contatto'
              : 'Visualizza i fornitori e le loro informazioni di contatto'}
          </p>
        </div>
        {user?.ruolo === 'admin' && (
          <button
            onClick={() => setShowForm(true)}
            className="mt-4 sm:mt-0 btn btn-primary"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nuovo Fornitore
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Cerca fornitori..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <select
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={selectedCategoria}
              onChange={(e) => setSelectedCategoria(e.target.value)}
            >
              <option value="">Tutte le categorie</option>
              {categorie.map(categoria => (
                <option key={categoria} value={categoria}>
                  {categoria.replace('_', ' ').toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Suppliers List */}
      <div className="space-y-6">
        {Object.keys(groupedFornitori).length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nessun fornitore trovato</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm || selectedCategoria ? 'Prova a modificare i filtri di ricerca.' : 'Aggiungi il tuo primo fornitore per iniziare.'}
            </p>
          </div>
        ) : (
          Object.entries(groupedFornitori).map(([categoria, fornitori]) => (
            <div key={categoria} className="bg-white shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-medium text-gray-900">
                  {categoria.replace('_', ' ').toUpperCase()} ({fornitori.length})
                </h2>
              </div>
              
              <div className="divide-y divide-gray-200">
                {fornitori.map((fornitore) => (
                  <div key={fornitore.id} className="p-6 hover:bg-gray-50">
                    <div className="flex flex-col gap-4">
                      <div className="flex-1">
                        <h3 className="text-base font-medium text-gray-900">{fornitore.nome}</h3>
                        <div className="mt-2 flex items-center text-sm text-gray-500">
                          <Phone className="h-4 w-4 mr-1 flex-shrink-0" />
                          <span className="break-all">{fornitore.telefono}</span>
                        </div>
                        {fornitore.whatsapp && (
                          <div className="mt-1 flex items-center text-sm text-gray-500">
                            <MessageCircle className="h-4 w-4 mr-1 flex-shrink-0" />
                            <span className="break-all">WhatsApp: {fornitore.whatsapp}</span>
                          </div>
                        )}
                        {Array.isArray(fornitore.categorie) && fornitore.categorie.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {fornitore.categorie.map(cat => (
                              <div key={cat} className="inline-flex items-center gap-1">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                  {cat.replace('_', ' ')}
                                </span>
                                {user?.ruolo === 'admin' && (
                                  <button
                                    onClick={() => setFornitoreDefault(cat, fornitore.id)}
                                    className={`p-1 rounded hover:bg-yellow-100 transition-colors ${
                                      fornitoriDefault[cat] === fornitore.id 
                                        ? 'text-yellow-500' 
                                        : 'text-gray-300 hover:text-yellow-400'
                                    }`}
                                    title={fornitoriDefault[cat] === fornitore.id ? 'Fornitore preferito' : 'Imposta come preferito'}
                                  >
                                    <Star className="h-4 w-4" fill={fornitoriDefault[cat] === fornitore.id ? 'currentColor' : 'none'} />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          onClick={() => callFornitore(fornitore.telefono)}
                          className="btn btn-outline btn-sm text-green-600 hover:bg-green-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                          title="Chiama"
                        >
                          <Phone className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => messageFornitore(fornitore.whatsapp || fornitore.telefono)}
                          className="btn btn-outline btn-sm text-green-600 hover:bg-green-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                          title="Messaggio WhatsApp"
                        >
                          <MessageCircle className="h-4 w-4" />
                        </button>
                        {user?.ruolo === 'admin' && (
                          <>
                            <button
                              onClick={() => editFornitore(fornitore)}
                              className="btn btn-outline btn-sm flex-shrink-0 flex items-center justify-center min-w-[44px]"
                            >
                              <Edit className="h-4 w-4 sm:mr-1" />
                              <span className="hidden sm:inline">Modifica</span>
                            </button>
                            <button
                              onClick={() => deleteFornitore(fornitore.id)}
                              className="btn btn-outline btn-sm text-red-600 hover:bg-red-50 flex-shrink-0 flex items-center justify-center min-w-[44px]"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Supplier Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowForm(false)} />
            
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleSubmit}>
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">
                      {editingFornitore ? 'Modifica Fornitore' : 'Nuovo Fornitore'}
                    </h3>
                    <button
                      type="button"
                      onClick={() => setShowForm(false)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      ×
                    </button>
                  </div>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Nome Fornitore
                      </label>
                      <input
                        type="text"
                        required
                        className="mt-1 input"
                        value={formData.nome}
                        onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                        placeholder="es. Mario Frutta"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Numero di Telefono
                      </label>
                      <input
                        type="tel"
                        required
                        className="mt-1 input"
                        value={formData.telefono}
                        onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                        placeholder="es. 3331234567"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Categorie {formData.categorie.length > 0 && `(${formData.categorie.length} selezionate)`}
                      </label>
                      {categorieDisponibili.length === 0 ? (
                        <div className="border border-yellow-300 rounded-md p-4 bg-yellow-50">
                          <p className="text-sm text-yellow-800 mb-2">
                            ⚠️ Nessuna categoria disponibile caricata.
                          </p>
                          <p className="text-xs text-yellow-700 mb-2">
                            Verifica che ci siano categorie attive in <strong>Categorie & Tipologie</strong>.
                          </p>
                          <button
                            type="button"
                            onClick={loadCategorieDisponibili}
                            className="text-xs text-yellow-800 underline hover:text-yellow-900"
                          >
                            Riprova a caricare categorie
                          </button>
                          <p className="text-xs text-gray-600 mt-2">
                            Debug: categorieDisponibili.length = {categorieDisponibili.length}
                          </p>
                        </div>
                      ) : (
                        <>
                          <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3">
                            {categorieDisponibili.map(cat => (
                              <label key={cat} className="flex items-center space-x-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={formData.categorie.includes(cat)}
                                  onChange={() => toggleCategoria(cat)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <span className="text-sm text-gray-700 capitalize">
                                  {cat.replace('_', ' ')}
                                </span>
                              </label>
                            ))}
                          </div>
                          {formData.categorie.length > 0 && (
                            <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded">
                              <p className="text-xs text-blue-800 font-medium mb-1">
                                ✓ Categorie selezionate ({formData.categorie.length}):
                              </p>
                              <div className="flex flex-wrap gap-1">
                                {formData.categorie.map(cat => (
                                  <span key={cat} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-600 text-white">
                                    {cat.replace('_', ' ')}
                                    <button
                                      type="button"
                                      onClick={() => toggleCategoria(cat)}
                                      className="ml-1 hover:bg-blue-700 rounded-full p-0.5"
                                    >
                                      ×
                                    </button>
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {formData.categorie.length === 0 && (
                            <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded">
                              <p className="text-xs text-red-800 font-medium">
                                ⚠️ Seleziona almeno una categoria usando le checkbox sopra
                              </p>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        WhatsApp (opzionale)
                      </label>
                      <input
                        type="tel"
                        className="mt-1 input"
                        value={formData.whatsapp}
                        onChange={(e) => setFormData({ ...formData, whatsapp: e.target.value })}
                        placeholder="es. 3331234567 (se diverso dal telefono)"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Se lasciato vuoto, verrà usato il numero di telefono
                      </p>
                    </div>

                    <div className="border-t border-gray-200 pt-4">
                      <p className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                        <MessageCircle className="h-4 w-4 mr-2" />
                        Personalizzazione Messaggi WhatsApp
                      </p>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Introduzione Messaggio
                          </label>
                          <textarea
                            rows="2"
                            className="mt-1 input"
                            value={formData.intro_custom}
                            onChange={(e) => setFormData({ ...formData, intro_custom: e.target.value })}
                            placeholder="Ciao, ecco l'ordine:"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Testo iniziale che apparirà all'inizio di ogni messaggio WhatsApp per questo fornitore
                          </p>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Chiusura Messaggio
                          </label>
                          <textarea
                            rows="2"
                            className="mt-1 input"
                            value={formData.outro_custom}
                            onChange={(e) => setFormData({ ...formData, outro_custom: e.target.value })}
                            placeholder="Grazie e a presto!"
                          />
                          <p className="mt-1 text-xs text-gray-500">
                            Testo finale che apparirà alla fine di ogni messaggio WhatsApp per questo fornitore
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="btn btn-primary"
                  >
                    {editingFornitore ? 'Aggiorna' : 'Crea'} Fornitore
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="mt-3 sm:mt-0 sm:mr-3 btn btn-outline"
                  >
                    Annulla
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modale di conferma eliminazione */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setFornitoreToDelete(null);
        }}
        title="Conferma Eliminazione"
        message={`Sei sicuro di voler eliminare il fornitore "${fornitoreToDelete?.nome}"?\n\nQuesta azione non può essere annullata.`}
        type="warning"
        confirmText="Sì, Elimina"
        cancelText="Annulla"
        onConfirm={executeDeleteFornitore}
      />
    </div>
  );
}

export default Fornitori;

